import { useState, useEffect, useCallback, useId, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  useBiblioRecord,
  useCreateBiblioRecord,
  useUpdateBiblioRecord,
  useAuthoritySearch,
  useRdaVocabularies,
} from "@/api/hooks";
import type {
  BiblioRecord,
  CreateBiblioRecordInput,
  RecordType,
  RecordStatus,
  ContributionRole,
  AuthoritySummary,
  RdaSuggestion,
  RdaVocabularies,
} from "@/api/types";
import { RECORD_TYPE_LABELS, RECORD_TYPE_ICONS } from "@/lib/recordTypes";
import { RdaTypePicker } from "../components/RdaTypePicker";
import { proposeRdaTypes } from "../rdaSuggestion";

// ============================================================================
// Types
// ============================================================================

type EditMode = "web" | "unimarc" | "bibframe";

interface BibframeProperty {
  uri: string;
  label: string;
  value: string;
  datatype?: "literal" | "uri" | "date";
}

interface BibframeEntity {
  type: "Work" | "Instance" | "Item";
  uri: string;
  properties: BibframeProperty[];
}

interface FormData {
  type: RecordType;
  title: string;
  subtitle: string;
  isbn: string;
  issn: string;
  ean: string;
  publisher: string;
  publicationDate: string;
  language: string;
  classification: string;
  subjects: string[];
  summary: string;
  pageCount: string;
  edition: string;
  series: string;
  seriesNumber: string;
  status: RecordStatus;
  contentTypes: string[];
  mediaTypes: string[];
  carrierTypes: string[];
  contributions: Array<{
    id?: string;
    authorityId: string;
    authority: AuthoritySummary;
    role: ContributionRole;
    position: number;
  }>;
}

interface UnimarcField {
  tag: string;
  ind1: string;
  ind2: string;
  subfields: Array<{ code: string; value: string }>;
}

// ============================================================================
// Constants
// ============================================================================

const LANGUAGES: Record<string, string> = {
  fre: "Français",
  eng: "Anglais",
  ger: "Allemand",
  spa: "Espagnol",
  ita: "Italien",
  por: "Portugais",
  dut: "Néerlandais",
  rus: "Russe",
  chi: "Chinois",
  jpn: "Japonais",
  ara: "Arabe",
  lat: "Latin",
};

const CONTRIBUTION_ROLES: Record<ContributionRole, string> = {
  author: "Auteur",
  coauthor: "Co-auteur",
  editor: "Éditeur scientifique",
  translator: "Traducteur",
  illustrator: "Illustrateur",
  photographer: "Photographe",
  composer: "Compositeur",
  performer: "Interprète",
  director: "Réalisateur",
  narrator: "Narrateur",
  preface: "Préfacier",
  publisher: "Éditeur",
  contributor: "Contributeur",
};

const STATUS_OPTIONS: Record<RecordStatus, string> = {
  draft: "Brouillon",
  validated: "Validée",
  archived: "Archivée",
};

const EMPTY_FORM: FormData = {
  type: "book",
  title: "",
  subtitle: "",
  isbn: "",
  issn: "",
  ean: "",
  publisher: "",
  publicationDate: "",
  language: "fre",
  classification: "",
  subjects: [],
  summary: "",
  pageCount: "",
  edition: "",
  series: "",
  seriesNumber: "",
  status: "draft",
  contentTypes: [],
  mediaTypes: [],
  carrierTypes: [],
  contributions: [],
};

const INPUT_CLASSES =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500";

// ============================================================================
// Main Component
// ============================================================================

export function RecordEditPage() {
  const { id } = useParams<{ id: string }>();
  // The `catalog/new` route has no `:id` param: the page then creates a record.
  const isNew = id === undefined;
  const navigate = useNavigate();
  const { data: record, isLoading, error } = useBiblioRecord(id);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const createMutation = useCreateBiblioRecord();
  const updateMutation = useUpdateBiblioRecord(id ?? "");
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const [mode, setMode] = useState<EditMode>("web");
  const [formData, setFormData] = useState<FormData | null>(isNew ? EMPTY_FORM : null);
  const [unimarcFields, setUnimarcFields] = useState<UnimarcField[]>([]);
  const [bibframeEntities, setBibframeEntities] = useState<BibframeEntity[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["description", "rda", "contributors", "subjects"])
  );

  // Initialize form data from record
  useEffect(() => {
    if (record && !formData) {
      setFormData({
        type: record.type,
        title: record.title,
        subtitle: record.subtitle ?? "",
        isbn: record.isbn ?? "",
        issn: record.issn ?? "",
        ean: record.ean ?? "",
        publisher: record.publisher ?? "",
        publicationDate: record.publicationDate ?? "",
        language: record.language,
        classification: record.classification ?? "",
        subjects: record.subjects,
        summary: record.summary ?? "",
        pageCount: record.pageCount?.toString() ?? "",
        edition: record.edition ?? "",
        series: record.series ?? "",
        seriesNumber: record.seriesNumber ?? "",
        status: record.status,
        contentTypes: record.contentTypes,
        mediaTypes: record.mediaTypes,
        carrierTypes: record.carrierTypes,
        contributions: record.contributions.map((c) => ({
          id: c.id,
          authorityId: c.authorityId,
          authority: c.authority,
          role: c.role,
          position: c.position,
        })),
      });
      setUnimarcFields(convertToUnimarc(record));
      setBibframeEntities(convertToBibframe(record));
    }
  }, [record, formData]);

  const handleFieldChange = useCallback(
    (field: keyof FormData, value: FormData[keyof FormData]) => {
      setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
      setHasChanges(true);
    },
    []
  );

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!formData) return;

    try {
      const input: CreateBiblioRecordInput = {
        type: formData.type,
        title: formData.title,
        language: formData.language,
        subjects: formData.subjects,
        status: formData.status,
        // Always sent so that removing every value clears the field.
        contentTypes: formData.contentTypes,
        mediaTypes: formData.mediaTypes,
        carrierTypes: formData.carrierTypes,
        contributions: formData.contributions.map((c) => ({
          authorityId: c.authorityId,
          role: c.role,
          position: c.position,
        })),
      };

      // Only include optional fields if they have values
      if (formData.subtitle) input.subtitle = formData.subtitle;
      if (formData.isbn) input.isbn = formData.isbn;
      if (formData.issn) input.issn = formData.issn;
      if (formData.ean) input.ean = formData.ean;
      if (formData.publisher) input.publisher = formData.publisher;
      if (formData.publicationDate) input.publicationDate = formData.publicationDate;
      if (formData.classification) input.classification = formData.classification;
      if (formData.summary) input.summary = formData.summary;
      if (formData.pageCount) input.pageCount = parseInt(formData.pageCount, 10);
      if (formData.edition) input.edition = formData.edition;
      if (formData.series) input.series = formData.series;
      if (formData.seriesNumber) input.seriesNumber = formData.seriesNumber;

      if (isNew) {
        const created = await createMutation.mutateAsync(input);
        void navigate(`/catalog/${created.id}`, { replace: true });
        return;
      }

      await updateMutation.mutateAsync(input);

      setSaveStatus("success");
      setHasChanges(false);
      window.setTimeout(() => {
        setSaveStatus("idle");
      }, 3000);
    } catch {
      setSaveStatus("error");
      window.setTimeout(() => {
        setSaveStatus("idle");
      }, 3000);
    }
  }, [formData, isNew, createMutation, updateMutation, navigate]);

  const handleCancel = useCallback(() => {
    const target = isNew ? "/catalog" : `/catalog/${id}`;
    if (hasChanges) {
      if (window.confirm("Voulez-vous vraiment annuler ? Les modifications seront perdues.")) {
        void navigate(target);
      }
    } else {
      void navigate(target);
    }
  }, [hasChanges, navigate, id, isNew]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !formData || (!isNew && !record)) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
        Notice introuvable ou erreur de chargement.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <header className="space-y-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/catalog" className="hover:text-blue-600">
            Catalogue
          </Link>
          <span>/</span>
          {record ? (
            <>
              <Link to={`/catalog/${record.id}`} className="hover:text-blue-600">
                {record.title}
              </Link>
              <span>/</span>
              <span className="text-gray-900 dark:text-white">Modifier</span>
            </>
          ) : (
            <span className="text-gray-900 dark:text-white">Nouvelle notice</span>
          )}
        </nav>

        {/* Title & Mode Switcher */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {record ? "Modifier la notice" : "Nouvelle notice"}
            </h1>
            {record && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {RECORD_TYPE_ICONS[record.type]} {RECORD_TYPE_LABELS[record.type]} — {record.title}
              </p>
            )}
          </div>

          {/* Mode Switcher: the UNIMARC and BIBFRAME views are derived from a saved record. */}
          {record && (
            <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
              <button
                onClick={() => {
                  setMode("web");
                }}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                  mode === "web"
                    ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                    />
                  </svg>
                  Interface Web
                </span>
              </button>
              <button
                onClick={() => {
                  setMode("unimarc");
                }}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                  mode === "unimarc"
                    ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    />
                  </svg>
                  UNIMARC
                </span>
              </button>
              <button
                onClick={() => {
                  setMode("bibframe");
                }}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                  mode === "bibframe"
                    ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  BIBFRAME
                </span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      {mode === "web" && (
        <WebEditor
          formData={formData}
          savedRecord={record}
          expandedSections={expandedSections}
          onFieldChange={handleFieldChange}
          onToggleSection={toggleSection}
        />
      )}
      {mode === "unimarc" && (
        <UnimarcEditor fields={unimarcFields} onFieldsChange={setUnimarcFields} />
      )}
      {mode === "bibframe" && (
        <BibframeEditor entities={bibframeEntities} onEntitiesChange={setBibframeEntities} />
      )}

      {/* Actions */}
      <footer className="sticky bottom-0 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            {saveStatus === "success" && (
              <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Notice enregistrée
              </span>
            )}
            {saveStatus === "error" && (
              <span className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Erreur lors de l'enregistrement
              </span>
            )}
            {saveStatus === "idle" && hasChanges && (
              <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                Modifications non enregistrées
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving || !formData.title.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// Web Editor Component
// ============================================================================

interface WebEditorProps {
  formData: FormData;
  /** Saved record when editing; undefined on creation. */
  savedRecord: BiblioRecord | undefined;
  expandedSections: Set<string>;
  onFieldChange: (field: keyof FormData, value: FormData[keyof FormData]) => void;
  onToggleSection: (section: string) => void;
}

function WebEditor({
  formData,
  savedRecord,
  expandedSections,
  onFieldChange,
  onToggleSection,
}: WebEditorProps) {
  return (
    <div className="space-y-4">
      {/* Type & Status */}
      <Section
        title="Informations générales"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        expanded={expandedSections.has("general")}
        onToggle={() => {
          onToggleSection("general");
        }}
        defaultExpanded
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Type de document" required>
            <select
              value={formData.type}
              onChange={(e) => {
                onFieldChange("type", e.target.value);
              }}
              className={INPUT_CLASSES}
            >
              {Object.entries(RECORD_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {RECORD_TYPE_ICONS[value as RecordType]} {label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Statut">
            <select
              value={formData.status}
              onChange={(e) => {
                onFieldChange("status", e.target.value);
              }}
              className={INPUT_CLASSES}
            >
              {Object.entries(STATUS_OPTIONS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </Section>

      {/* RDA-FR content and carrier */}
      <Section
        title="Contenu et support (RDA-FR)"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        }
        expanded={expandedSections.has("rda")}
        onToggle={() => {
          onToggleSection("rda");
        }}
      >
        <RdaTypesEditor
          formData={formData}
          savedRecord={savedRecord}
          onFieldChange={onFieldChange}
        />
      </Section>

      {/* Description */}
      <Section
        title="Description"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        }
        expanded={expandedSections.has("description")}
        onToggle={() => {
          onToggleSection("description");
        }}
      >
        <div className="space-y-4">
          <FormField label="Titre" required>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                onFieldChange("title", e.target.value);
              }}
              className={INPUT_CLASSES}
              placeholder="Titre principal de l'ouvrage"
            />
          </FormField>

          <FormField label="Sous-titre">
            <input
              type="text"
              value={formData.subtitle}
              onChange={(e) => {
                onFieldChange("subtitle", e.target.value);
              }}
              className={INPUT_CLASSES}
              placeholder="Sous-titre ou complément de titre"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Éditeur">
              <input
                type="text"
                value={formData.publisher}
                onChange={(e) => {
                  onFieldChange("publisher", e.target.value);
                }}
                className={INPUT_CLASSES}
                placeholder="Nom de l'éditeur"
              />
            </FormField>
            <FormField label="Date de publication">
              <input
                type="date"
                value={formData.publicationDate}
                onChange={(e) => {
                  onFieldChange("publicationDate", e.target.value);
                }}
                className={INPUT_CLASSES}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField label="Langue">
              <select
                value={formData.language}
                onChange={(e) => {
                  onFieldChange("language", e.target.value);
                }}
                className={INPUT_CLASSES}
              >
                {Object.entries(LANGUAGES).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Nombre de pages">
              <input
                type="number"
                value={formData.pageCount}
                onChange={(e) => {
                  onFieldChange("pageCount", e.target.value);
                }}
                className={INPUT_CLASSES}
                placeholder="320"
              />
            </FormField>
            <FormField label="Édition">
              <input
                type="text"
                value={formData.edition}
                onChange={(e) => {
                  onFieldChange("edition", e.target.value);
                }}
                className={INPUT_CLASSES}
                placeholder="2e édition"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Collection">
              <input
                type="text"
                value={formData.series}
                onChange={(e) => {
                  onFieldChange("series", e.target.value);
                }}
                className={INPUT_CLASSES}
                placeholder="Nom de la collection"
              />
            </FormField>
            <FormField label="N° dans la collection">
              <input
                type="text"
                value={formData.seriesNumber}
                onChange={(e) => {
                  onFieldChange("seriesNumber", e.target.value);
                }}
                className={INPUT_CLASSES}
                placeholder="42"
              />
            </FormField>
          </div>

          <FormField label="Résumé">
            <textarea
              value={formData.summary}
              onChange={(e) => {
                onFieldChange("summary", e.target.value);
              }}
              className={`${INPUT_CLASSES} min-h-[120px] resize-y`}
              placeholder="Résumé ou description du contenu..."
            />
          </FormField>
        </div>
      </Section>

      {/* Identifiers */}
      <Section
        title="Identifiants"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        }
        expanded={expandedSections.has("identifiers")}
        onToggle={() => {
          onToggleSection("identifiers");
        }}
      >
        <div className="grid grid-cols-3 gap-4">
          <FormField label="ISBN">
            <input
              type="text"
              value={formData.isbn}
              onChange={(e) => {
                onFieldChange("isbn", e.target.value);
              }}
              className={`${INPUT_CLASSES} font-mono`}
              placeholder="978-2-07-036822-8"
            />
          </FormField>
          <FormField label="ISSN">
            <input
              type="text"
              value={formData.issn}
              onChange={(e) => {
                onFieldChange("issn", e.target.value);
              }}
              className={`${INPUT_CLASSES} font-mono`}
              placeholder="1234-5678"
            />
          </FormField>
          <FormField label="EAN">
            <input
              type="text"
              value={formData.ean}
              onChange={(e) => {
                onFieldChange("ean", e.target.value);
              }}
              className={`${INPUT_CLASSES} font-mono`}
              placeholder="9782070368228"
            />
          </FormField>
        </div>
        <FormField label="Classification" className="mt-4">
          <input
            type="text"
            value={formData.classification}
            onChange={(e) => {
              onFieldChange("classification", e.target.value);
            }}
            className={`${INPUT_CLASSES} font-mono`}
            placeholder="843.914"
          />
        </FormField>
      </Section>

      {/* Contributors */}
      <Section
        title="Auteurs et contributeurs"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        }
        expanded={expandedSections.has("contributors")}
        onToggle={() => {
          onToggleSection("contributors");
        }}
      >
        <ContributorsEditor
          contributions={formData.contributions}
          onChange={(contributions) => {
            onFieldChange("contributions", contributions);
          }}
        />
      </Section>

      {/* Subjects */}
      <Section
        title="Sujets et indexation"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        }
        expanded={expandedSections.has("subjects")}
        onToggle={() => {
          onToggleSection("subjects");
        }}
      >
        <SubjectsEditor
          subjects={formData.subjects}
          onChange={(subjects) => {
            onFieldChange("subjects", subjects);
          }}
        />
      </Section>
    </div>
  );
}

// ============================================================================
// RDA-FR Types Editor
// ============================================================================

interface RdaTypesEditorProps {
  formData: FormData;
  savedRecord: BiblioRecord | undefined;
  onFieldChange: (field: keyof FormData, value: FormData[keyof FormData]) => void;
}

const RDA_ELEMENTS = [
  { field: "contentTypes", label: "Type de contenu", unimarcTag: "181" },
  { field: "mediaTypes", label: "Type de médiation", unimarcTag: "182" },
  { field: "carrierTypes", label: "Type de support", unimarcTag: "183" },
] as const;

function RdaTypesEditor({ formData, savedRecord, onFieldChange }: RdaTypesEditorProps) {
  const { data: vocabularies, isLoading, error } = useRdaVocabularies();
  const [justApplied, setJustApplied] = useState(false);
  const appliedRef = useRef<HTMLParagraphElement>(null);

  const proposal = useMemo(
    () =>
      vocabularies
        ? proposeRdaTypes({
            current: formData,
            documentType: formData.type,
            saved: savedRecord,
            defaultsByDocumentType: vocabularies.defaultsByDocumentType,
          })
        : null,
    [vocabularies, formData, savedRecord]
  );

  // The apply button disappears with the proposal: keep keyboard focus nearby.
  useEffect(() => {
    if (justApplied) appliedRef.current?.focus();
  }, [justApplied]);

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
        Chargement des vocabulaires RDA-FR…
      </p>
    );
  }

  if (error || !vocabularies) {
    return (
      <p className="text-sm text-red-700 dark:text-red-400" role="alert">
        Les vocabulaires RDA-FR n&apos;ont pas pu être chargés. Les valeurs actuelles sont
        conservées à l&apos;enregistrement.
      </p>
    );
  }

  const applyProposal = (values: RdaSuggestion) => {
    for (const { field } of RDA_ELEMENTS) {
      if (values[field].length > 0) onFieldChange(field, values[field]);
    }
    setJustApplied(true);
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Éléments de la Transition bibliographique, exportés en UNIMARC 181, 182 et 183. Chaque
        élément accepte plusieurs valeurs, par exemple un livre accompagné d&apos;un CD.
      </p>

      {proposal ? (
        <RdaProposal
          proposal={proposal}
          vocabularies={vocabularies}
          onApply={() => {
            applyProposal(proposal);
          }}
        />
      ) : (
        justApplied && (
          <p
            ref={appliedRef}
            tabIndex={-1}
            role="status"
            className="text-sm text-gray-600 focus:outline-none dark:text-gray-300"
          >
            Proposition appliquée. Vérifiez les valeurs puis enregistrez la notice.
          </p>
        )
      )}

      <div className="grid gap-5 md:grid-cols-3">
        {RDA_ELEMENTS.map(({ field, label, unimarcTag }) => (
          <RdaTypePicker
            key={field}
            label={label}
            unimarcTag={unimarcTag}
            vocabulary={vocabularies[field]}
            values={formData[field]}
            onChange={(values) => {
              onFieldChange(field, values);
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface RdaProposalProps {
  proposal: RdaSuggestion;
  vocabularies: RdaVocabularies;
  onApply: () => void;
}

/**
 * Values proposed for the empty RDA-FR elements, with what they are based on.
 * Nothing changes until the cataloguer applies them.
 */
function RdaProposal({ proposal, vocabularies, onApply }: RdaProposalProps) {
  const headingId = useId();

  const basis = proposal.fromSourceRecord
    ? proposal.inferred
      ? "D’après les zones 181 à 183 de la notice importée, complétées par déduction."
      : "D’après les zones 181 à 183 de la notice importée."
    : "Déduite du type de document et des données codées de la notice.";

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900 dark:bg-blue-950/30"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <h4 id={headingId} className="text-sm font-medium text-gray-900 dark:text-white">
            Proposition pour les éléments non renseignés
          </h4>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
            {RDA_ELEMENTS.filter(({ field }) => proposal[field].length > 0).map(
              ({ field, label }) => (
                <div key={field} className="contents">
                  <dt className="text-gray-600 dark:text-gray-400">{label}</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    {proposal[field]
                      .map(
                        (code) =>
                          vocabularies[field].find((entry) => entry.code === code)?.label ?? code
                      )
                      .join(", ")}
                  </dd>
                </div>
              )
            )}
          </dl>
          <p className="text-xs text-gray-600 dark:text-gray-400">{basis}</p>
        </div>
        <button
          type="button"
          onClick={onApply}
          className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:focus-visible:ring-offset-gray-800"
        >
          Appliquer la proposition
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Section Component
// ============================================================================

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function Section({ title, icon, expanded, onToggle, children, defaultExpanded }: SectionProps) {
  const isExpanded = defaultExpanded || expanded;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
      >
        <div className="flex items-center gap-3">
          <span className="text-gray-400 dark:text-gray-500">{icon}</span>
          <h2 className="font-medium text-gray-900 dark:text-white">{title}</h2>
        </div>
        <svg
          className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="border-t border-gray-200 p-5 dark:border-gray-700">{children}</div>
      )}
    </div>
  );
}

// ============================================================================
// Form Field Component
// ============================================================================

interface FormFieldProps {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

function FormField({ label, required, className, children }: FormFieldProps) {
  return (
    <label className={`block ${className ?? ""}`}>
      <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

// ============================================================================
// Contributors Editor
// ============================================================================

interface ContributorsEditorProps {
  contributions: FormData["contributions"];
  onChange: (contributions: FormData["contributions"]) => void;
}

function ContributorsEditor({ contributions, onChange }: ContributorsEditorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const { data: searchResults } = useAuthoritySearch({ q: searchQuery, type: "person", limit: 10 });

  const addContribution = useCallback(
    (authority: AuthoritySummary) => {
      onChange([
        ...contributions,
        {
          authorityId: authority.id,
          authority,
          role: "author",
          position: contributions.length,
        },
      ]);
      setSearchQuery("");
      setShowSearch(false);
    },
    [contributions, onChange]
  );

  const removeContribution = useCallback(
    (index: number) => {
      onChange(contributions.filter((_, i) => i !== index));
    },
    [contributions, onChange]
  );

  const updateRole = useCallback(
    (index: number, role: ContributionRole) => {
      onChange(contributions.map((c, i) => (i === index ? { ...c, role } : c)));
    },
    [contributions, onChange]
  );

  return (
    <div className="space-y-3">
      {contributions.length > 0 && (
        <div className="space-y-2">
          {contributions.map((contribution, index) => (
            <div
              key={contribution.authorityId}
              className="flex items-center gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                {contribution.authority.preferredName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900 dark:text-white">
                  {contribution.authority.preferredName}
                </p>
              </div>
              <select
                value={contribution.role}
                onChange={(e) => {
                  updateRole(index, e.target.value as ContributionRole);
                }}
                className="rounded-md border-gray-300 text-sm dark:border-gray-600 dark:bg-gray-700"
              >
                {Object.entries(CONTRIBUTION_ROLES).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  removeContribution(index);
                }}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-200 hover:text-red-500 dark:hover:bg-gray-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add contributor */}
      {showSearch ? (
        <div className="space-y-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            placeholder="Rechercher un auteur..."
            className={INPUT_CLASSES}
            autoFocus
          />
          {searchResults && searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              {searchResults.map((authority) => (
                <button
                  key={authority.id}
                  onClick={() => {
                    addContribution(authority);
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <span className="text-gray-900 dark:text-white">{authority.preferredName}</span>
                  {authority.dateRange && (
                    <span className="text-sm text-gray-500">({authority.dateRange})</span>
                  )}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => {
              setShowSearch(false);
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Annuler
          </button>
        </div>
      ) : (
        <button
          onClick={() => {
            setShowSearch(true);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter un contributeur
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Subjects Editor
// ============================================================================

interface SubjectsEditorProps {
  subjects: string[];
  onChange: (subjects: string[]) => void;
}

function SubjectsEditor({ subjects, onChange }: SubjectsEditorProps) {
  const [newSubject, setNewSubject] = useState("");

  const addSubject = useCallback(() => {
    const trimmed = newSubject.trim();
    if (trimmed && !subjects.includes(trimmed)) {
      onChange([...subjects, trimmed]);
      setNewSubject("");
    }
  }, [newSubject, subjects, onChange]);

  const removeSubject = useCallback(
    (index: number) => {
      onChange(subjects.filter((_, i) => i !== index));
    },
    [subjects, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addSubject();
      }
    },
    [addSubject]
  );

  return (
    <div className="space-y-3">
      {subjects.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {subjects.map((subject, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 py-1 pr-2 pl-3 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {subject}
              <button
                onClick={() => {
                  removeSubject(index);
                }}
                className="rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={newSubject}
          onChange={(e) => {
            setNewSubject(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Ajouter un sujet..."
          className={`${INPUT_CLASSES} flex-1`}
        />
        <button
          onClick={addSubject}
          disabled={!newSubject.trim()}
          className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          Ajouter
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// UNIMARC Editor
// ============================================================================

interface UnimarcEditorProps {
  fields: UnimarcField[];
  onFieldsChange: (fields: UnimarcField[]) => void;
}

function UnimarcEditor({ fields, onFieldsChange }: UnimarcEditorProps) {
  const [newFieldTag, setNewFieldTag] = useState("");

  const addField = useCallback(() => {
    if (newFieldTag.length === 3) {
      onFieldsChange([
        ...fields,
        { tag: newFieldTag, ind1: " ", ind2: " ", subfields: [{ code: "a", value: "" }] },
      ]);
      setNewFieldTag("");
    }
  }, [newFieldTag, fields, onFieldsChange]);

  const removeField = useCallback(
    (index: number) => {
      onFieldsChange(fields.filter((_, i) => i !== index));
    },
    [fields, onFieldsChange]
  );

  const updateField = useCallback(
    (index: number, updates: Partial<UnimarcField>) => {
      onFieldsChange(fields.map((f, i) => (i === index ? { ...f, ...updates } : f)));
    },
    [fields, onFieldsChange]
  );

  const updateSubfield = useCallback(
    (fieldIndex: number, subfieldIndex: number, value: string) => {
      onFieldsChange(
        fields.map((f, i) =>
          i === fieldIndex
            ? {
                ...f,
                subfields: f.subfields.map((sf, j) =>
                  j === subfieldIndex ? { ...sf, value } : sf
                ),
              }
            : f
        )
      );
    },
    [fields, onFieldsChange]
  );

  const addSubfield = useCallback(
    (fieldIndex: number) => {
      onFieldsChange(
        fields.map((f, i) =>
          i === fieldIndex ? { ...f, subfields: [...f.subfields, { code: "a", value: "" }] } : f
        )
      );
    },
    [fields, onFieldsChange]
  );

  const removeSubfield = useCallback(
    (fieldIndex: number, subfieldIndex: number) => {
      onFieldsChange(
        fields.map((f, i) =>
          i === fieldIndex
            ? { ...f, subfields: f.subfields.filter((_, j) => j !== subfieldIndex) }
            : f
        )
      );
    },
    [fields, onFieldsChange]
  );

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.tag.localeCompare(b.tag)),
    [fields]
  );

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
        <svg
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="text-sm text-blue-800 dark:text-blue-300">
          <p className="font-medium">Mode UNIMARC</p>
          <p className="mt-1 text-blue-700 dark:text-blue-400">
            Édition directe des champs MARC. Utilisez ce mode si vous êtes familier avec le format
            UNIMARC.
          </p>
        </div>
      </div>

      {/* Fields list */}
      <div className="space-y-2">
        {sortedFields.map((field, fieldIndex) => (
          <div
            key={fieldIndex}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-700/50">
              <span className="font-mono text-sm font-bold text-blue-600 dark:text-blue-400">
                {field.tag}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {getFieldLabel(field.tag)}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <input
                  type="text"
                  value={field.ind1}
                  onChange={(e) => {
                    updateField(fieldIndex, { ind1: e.target.value.slice(0, 1) || " " });
                  }}
                  className="h-6 w-6 rounded border border-gray-300 text-center font-mono text-xs dark:border-gray-600 dark:bg-gray-700"
                  maxLength={1}
                  title="Indicateur 1"
                />
                <input
                  type="text"
                  value={field.ind2}
                  onChange={(e) => {
                    updateField(fieldIndex, { ind2: e.target.value.slice(0, 1) || " " });
                  }}
                  className="h-6 w-6 rounded border border-gray-300 text-center font-mono text-xs dark:border-gray-600 dark:bg-gray-700"
                  maxLength={1}
                  title="Indicateur 2"
                />
                <button
                  onClick={() => {
                    removeField(fieldIndex);
                  }}
                  className="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30"
                  title="Supprimer le champ"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="space-y-1 p-3">
              {field.subfields.map((subfield, subfieldIndex) => (
                <div key={subfieldIndex} className="flex items-center gap-2">
                  <span className="w-8 flex-shrink-0 text-right font-mono text-sm text-amber-600 dark:text-amber-400">
                    ${subfield.code}
                  </span>
                  <input
                    type="text"
                    value={subfield.value}
                    onChange={(e) => {
                      updateSubfield(fieldIndex, subfieldIndex, e.target.value);
                    }}
                    className={`${INPUT_CLASSES} flex-1 font-mono`}
                  />
                  {field.subfields.length > 1 && (
                    <button
                      onClick={() => {
                        removeSubfield(fieldIndex, subfieldIndex);
                      }}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500 dark:hover:bg-gray-700"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => {
                  addSubfield(fieldIndex);
                }}
                className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Ajouter un sous-champ
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new field */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newFieldTag}
          onChange={(e) => {
            setNewFieldTag(e.target.value.replace(/\D/g, "").slice(0, 3));
          }}
          placeholder="Tag (ex: 200)"
          className={`${INPUT_CLASSES} w-24 font-mono`}
          maxLength={3}
        />
        <button
          onClick={addField}
          disabled={newFieldTag.length !== 3}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter un champ
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// BIBFRAME Editor
// ============================================================================

interface BibframeEditorProps {
  entities: BibframeEntity[];
  onEntitiesChange: (entities: BibframeEntity[]) => void;
}

const BIBFRAME_NAMESPACES = {
  bf: "http://id.loc.gov/ontologies/bibframe/",
  bflc: "http://id.loc.gov/ontologies/bflc/",
  rdfs: "http://www.w3.org/2000/01/rdf-schema#",
  rdf: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
};

const BIBFRAME_PROPERTY_LABELS: Record<string, string> = {
  "bf:title": "Titre",
  "bf:mainTitle": "Titre principal",
  "bf:subtitle": "Sous-titre",
  "bf:contribution": "Contribution",
  "bf:agent": "Agent",
  "bf:role": "Rôle",
  "bf:subject": "Sujet",
  "bf:language": "Langue",
  "bf:summary": "Résumé",
  "bf:classification": "Classification",
  "bf:identifiedBy": "Identifiant",
  "bf:Isbn": "ISBN",
  "bf:Issn": "ISSN",
  "bf:Ean": "EAN",
  "bf:provisionActivity": "Publication",
  "bf:publisher": "Éditeur",
  "bf:date": "Date",
  "bf:place": "Lieu",
  "bf:extent": "Étendue",
  "bf:note": "Note",
  "bf:seriesStatement": "Collection",
  "bf:seriesEnumeration": "Numéro de collection",
  "bf:editionStatement": "Mention d'édition",
  "bf:hasItem": "Exemplaire",
  "bf:heldBy": "Détenu par",
  "bf:itemOf": "Exemplaire de",
  "bf:shelfMark": "Cote",
  "bf:barcode": "Code-barres",
  "bf:status": "Statut",
};

const ENTITY_COLORS: Record<BibframeEntity["type"], { bg: string; border: string; text: string }> =
  {
    Work: {
      bg: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-200 dark:border-purple-800",
      text: "text-purple-700 dark:text-purple-300",
    },
    Instance: {
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
      text: "text-blue-700 dark:text-blue-300",
    },
    Item: {
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
      text: "text-green-700 dark:text-green-300",
    },
  };

function BibframeEditor({ entities, onEntitiesChange }: BibframeEditorProps) {
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(
    new Set(entities.map((e) => e.uri))
  );

  const toggleEntity = useCallback((uri: string) => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(uri)) {
        next.delete(uri);
      } else {
        next.add(uri);
      }
      return next;
    });
  }, []);

  const updateProperty = useCallback(
    (entityUri: string, propertyUri: string, value: string) => {
      onEntitiesChange(
        entities.map((entity) =>
          entity.uri === entityUri
            ? {
                ...entity,
                properties: entity.properties.map((prop) =>
                  prop.uri === propertyUri ? { ...prop, value } : prop
                ),
              }
            : entity
        )
      );
    },
    [entities, onEntitiesChange]
  );

  const addProperty = useCallback(
    (entityUri: string, propertyUri: string) => {
      onEntitiesChange(
        entities.map((entity) =>
          entity.uri === entityUri
            ? {
                ...entity,
                properties: [
                  ...entity.properties,
                  {
                    uri: propertyUri,
                    label:
                      BIBFRAME_PROPERTY_LABELS[propertyUri] ??
                      propertyUri.split(":")[1] ??
                      propertyUri,
                    value: "",
                    datatype: "literal",
                  },
                ],
              }
            : entity
        )
      );
    },
    [entities, onEntitiesChange]
  );

  const removeProperty = useCallback(
    (entityUri: string, propertyIndex: number) => {
      onEntitiesChange(
        entities.map((entity) =>
          entity.uri === entityUri
            ? {
                ...entity,
                properties: entity.properties.filter((_, i) => i !== propertyIndex),
              }
            : entity
        )
      );
    },
    [entities, onEntitiesChange]
  );

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-900 dark:bg-purple-900/20">
        <svg
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-purple-600 dark:text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
        <div className="text-sm text-purple-800 dark:text-purple-300">
          <p className="font-medium">Mode BIBFRAME (Linked Data)</p>
          <p className="mt-1 text-purple-700 dark:text-purple-400">
            Édition selon le modèle BIBFRAME 2.0 de la Library of Congress. Structure en trois
            niveaux : Work (œuvre), Instance (manifestation) et Item (exemplaire).
          </p>
        </div>
      </div>

      {/* Namespace legend */}
      <div className="flex flex-wrap gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs dark:border-gray-700 dark:bg-gray-800">
        <span className="font-medium text-gray-600 dark:text-gray-400">Préfixes :</span>
        {Object.entries(BIBFRAME_NAMESPACES).map(([prefix, uri]) => (
          <span key={prefix} className="text-gray-500 dark:text-gray-500">
            <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
              {prefix}:
            </span>{" "}
            <span className="text-gray-400">{uri}</span>
          </span>
        ))}
      </div>

      {/* Entities */}
      <div className="space-y-4">
        {entities.map((entity) => {
          const colors = ENTITY_COLORS[entity.type];
          const isExpanded = expandedEntities.has(entity.uri);

          return (
            <div
              key={entity.uri}
              className={`overflow-hidden rounded-xl border-2 ${colors.border} ${colors.bg}`}
            >
              {/* Entity header */}
              <button
                onClick={() => {
                  toggleEntity(entity.uri);
                }}
                className="flex w-full items-center justify-between px-5 py-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-lg px-3 py-1 text-sm font-bold ${colors.text} bg-white/60 dark:bg-gray-900/40`}
                  >
                    bf:{entity.type}
                  </span>
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {entity.uri}
                  </span>
                </div>
                <svg
                  className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Properties */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                  <div className="space-y-3">
                    {entity.properties.map((prop, propIndex) => (
                      <div
                        key={`${prop.uri}-${String(propIndex)}`}
                        className="flex items-start gap-3"
                      >
                        <div className="w-40 flex-shrink-0 pt-2">
                          <span className="font-mono text-sm font-medium text-amber-600 dark:text-amber-400">
                            {prop.uri}
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{prop.label}</p>
                        </div>
                        <div className="flex flex-1 items-center gap-2">
                          {prop.datatype === "uri" ? (
                            <div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                />
                              </svg>
                              <input
                                type="text"
                                value={prop.value}
                                onChange={(e) => {
                                  updateProperty(entity.uri, prop.uri, e.target.value);
                                }}
                                className="flex-1 bg-transparent font-mono text-sm text-gray-700 outline-none dark:text-gray-300"
                                placeholder="URI..."
                              />
                            </div>
                          ) : (
                            <input
                              type={prop.datatype === "date" ? "date" : "text"}
                              value={prop.value}
                              onChange={(e) => {
                                updateProperty(entity.uri, prop.uri, e.target.value);
                              }}
                              className={INPUT_CLASSES}
                            />
                          )}
                          <button
                            onClick={() => {
                              removeProperty(entity.uri, propIndex);
                            }}
                            className="rounded p-1.5 text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30"
                            title="Supprimer cette propriété"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add property */}
                  <div className="mt-4 flex items-center gap-2">
                    <select
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) {
                          addProperty(entity.uri, e.target.value);
                          e.target.value = "";
                        }
                      }}
                    >
                      <option value="" disabled>
                        Ajouter une propriété...
                      </option>
                      {getAvailableProperties(entity.type).map((prop) => (
                        <option key={prop} value={prop}>
                          {prop} — {BIBFRAME_PROPERTY_LABELS[prop] ?? prop}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* RDF/XML Preview */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-white">Aperçu RDF/XML</h3>
          <button
            onClick={() => {
              const xml = generateRdfXml(entities);
              void navigator.clipboard.writeText(xml);
            }}
            className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Copier
          </button>
        </div>
        <pre className="max-h-64 overflow-auto p-4 font-mono text-xs text-gray-700 dark:text-gray-300">
          {generateRdfXml(entities)}
        </pre>
      </div>
    </div>
  );
}

function getAvailableProperties(entityType: BibframeEntity["type"]): string[] {
  const workProperties = [
    "bf:title",
    "bf:mainTitle",
    "bf:subtitle",
    "bf:contribution",
    "bf:subject",
    "bf:language",
    "bf:summary",
    "bf:classification",
    "bf:note",
  ];

  const instanceProperties = [
    "bf:title",
    "bf:identifiedBy",
    "bf:provisionActivity",
    "bf:extent",
    "bf:editionStatement",
    "bf:seriesStatement",
    "bf:seriesEnumeration",
    "bf:note",
  ];

  const itemProperties = ["bf:heldBy", "bf:shelfMark", "bf:barcode", "bf:status", "bf:note"];

  switch (entityType) {
    case "Work":
      return workProperties;
    case "Instance":
      return instanceProperties;
    case "Item":
      return itemProperties;
    default:
      return [];
  }
}

function generateRdfXml(entities: BibframeEntity[]): string {
  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"',
    '         xmlns:rdfs="http://www.w3.org/2000/01/rdf-schema#"',
    '         xmlns:bf="http://id.loc.gov/ontologies/bibframe/"',
    '         xmlns:bflc="http://id.loc.gov/ontologies/bflc/">',
    "",
  ];

  for (const entity of entities) {
    lines.push(`  <bf:${entity.type} rdf:about="${entity.uri}">`);
    for (const prop of entity.properties) {
      const propName = prop.uri.replace("bf:", "");
      if (prop.datatype === "uri" && prop.value) {
        lines.push(`    <bf:${propName} rdf:resource="${prop.value}"/>`);
      } else if (prop.value) {
        lines.push(`    <bf:${propName}>${escapeXml(prop.value)}</bf:${propName}>`);
      }
    }
    lines.push(`  </bf:${entity.type}>`);
    lines.push("");
  }

  lines.push("</rdf:RDF>");
  return lines.join("\n");
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ============================================================================
// Helpers
// ============================================================================

function convertToUnimarc(record: BiblioRecord): UnimarcField[] {
  const fields: UnimarcField[] = [];

  // 001 - Record identifier
  fields.push({
    tag: "001",
    ind1: " ",
    ind2: " ",
    subfields: [{ code: "a", value: record.id }],
  });

  // 010 - ISBN
  if (record.isbn) {
    fields.push({
      tag: "010",
      ind1: " ",
      ind2: " ",
      subfields: [{ code: "a", value: record.isbn }],
    });
  }

  // 011 - ISSN
  if (record.issn) {
    fields.push({
      tag: "011",
      ind1: " ",
      ind2: " ",
      subfields: [{ code: "a", value: record.issn }],
    });
  }

  // 101 - Language
  fields.push({
    tag: "101",
    ind1: "0",
    ind2: " ",
    subfields: [{ code: "a", value: record.language }],
  });

  // 181/182/183 - RDA-FR content, media and carrier types
  const rdaFields: Array<[string, string, string, string[]]> = [
    ["181", "c", "rdacontent", record.contentTypes],
    ["182", "c", "rdamedia", record.mediaTypes],
    ["183", "a", "rdacarrier", record.carrierTypes],
  ];
  rdaFields.forEach(([tag, code, source, values]) => {
    values.forEach((value) => {
      fields.push({
        tag,
        ind1: " ",
        ind2: " ",
        subfields: [
          { code, value },
          { code: "2", value: source },
        ],
      });
    });
  });

  // 200 - Title
  const titleSubfields: Array<{ code: string; value: string }> = [
    { code: "a", value: record.title },
  ];
  if (record.subtitle) {
    titleSubfields.push({ code: "e", value: record.subtitle });
  }
  fields.push({
    tag: "200",
    ind1: "1",
    ind2: " ",
    subfields: titleSubfields,
  });

  // 210 - Publication
  if (record.publisher || record.publicationYear) {
    const pubSubfields: Array<{ code: string; value: string }> = [];
    if (record.publisher) pubSubfields.push({ code: "c", value: record.publisher });
    if (record.publicationYear) pubSubfields.push({ code: "d", value: record.publicationYear });
    if (pubSubfields.length > 0) {
      fields.push({
        tag: "210",
        ind1: " ",
        ind2: " ",
        subfields: pubSubfields,
      });
    }
  }

  // 215 - Physical description
  if (record.pageCount) {
    fields.push({
      tag: "215",
      ind1: " ",
      ind2: " ",
      subfields: [{ code: "a", value: `${String(record.pageCount)} p.` }],
    });
  }

  // 225 - Series
  if (record.series) {
    const seriesSubfields: Array<{ code: string; value: string }> = [
      { code: "a", value: record.series },
    ];
    if (record.seriesNumber) {
      seriesSubfields.push({ code: "v", value: record.seriesNumber });
    }
    fields.push({
      tag: "225",
      ind1: " ",
      ind2: " ",
      subfields: seriesSubfields,
    });
  }

  // 330 - Summary
  if (record.summary) {
    fields.push({
      tag: "330",
      ind1: " ",
      ind2: " ",
      subfields: [{ code: "a", value: record.summary }],
    });
  }

  // 606 - Subjects
  record.subjects.forEach((subject) => {
    fields.push({
      tag: "606",
      ind1: " ",
      ind2: " ",
      subfields: [{ code: "a", value: subject }],
    });
  });

  // 676 - Classification
  if (record.classification) {
    fields.push({
      tag: "676",
      ind1: " ",
      ind2: " ",
      subfields: [{ code: "a", value: record.classification }],
    });
  }

  // 700/701/702 - Authors/Contributors
  record.contributions.forEach((contribution, index) => {
    const tag = index === 0 ? "700" : "701";
    fields.push({
      tag,
      ind1: " ",
      ind2: "1",
      subfields: [
        { code: "a", value: contribution.authority.preferredName },
        { code: "4", value: contribution.unimarcFunctionCode },
      ],
    });
  });

  return fields;
}

function getFieldLabel(tag: string): string {
  const labels: Record<string, string> = {
    "001": "Identifiant de notice",
    "010": "ISBN",
    "011": "ISSN",
    "020": "Numéro national de bibliographie",
    "100": "Données générales de traitement",
    "101": "Langue du document",
    "181": "Type de contenu (RDA-FR)",
    "182": "Type de médiation (RDA-FR)",
    "183": "Type de support (RDA-FR)",
    "102": "Pays de publication",
    "105": "Zone de données codées",
    "200": "Titre et mention de responsabilité",
    "205": "Mention d'édition",
    "210": "Publication, production, diffusion",
    "215": "Description matérielle",
    "225": "Collection",
    "300": "Note générale",
    "330": "Résumé ou extrait",
    "606": "Sujet - Nom commun",
    "607": "Sujet - Nom géographique",
    "676": "Classification Dewey",
    "700": "Responsabilité principale - Nom de personne",
    "701": "Coresponsabilité - Nom de personne",
    "702": "Responsabilité secondaire - Nom de personne",
    "710": "Responsabilité principale - Collectivité",
    "801": "Source de catalogage",
  };
  return labels[tag] ?? "";
}

function convertToBibframe(record: BiblioRecord): BibframeEntity[] {
  const baseUri = `http://example.org/resources/${record.id}`;
  const entities: BibframeEntity[] = [];

  // bf:Work - The intellectual content
  const workProperties: BibframeProperty[] = [
    { uri: "bf:mainTitle", label: "Titre principal", value: record.title, datatype: "literal" },
  ];

  if (record.subtitle) {
    workProperties.push({
      uri: "bf:subtitle",
      label: "Sous-titre",
      value: record.subtitle,
      datatype: "literal",
    });
  }

  // Add contributors
  record.contributions.forEach((contribution) => {
    workProperties.push({
      uri: "bf:contribution",
      label: "Contribution",
      value: contribution.authority.preferredName,
      datatype: "literal",
    });
  });

  // Add subjects
  record.subjects.forEach((subject) => {
    workProperties.push({
      uri: "bf:subject",
      label: "Sujet",
      value: subject,
      datatype: "literal",
    });
  });

  if (record.language) {
    workProperties.push({
      uri: "bf:language",
      label: "Langue",
      value: `http://id.loc.gov/vocabulary/languages/${record.language}`,
      datatype: "uri",
    });
  }

  if (record.summary) {
    workProperties.push({
      uri: "bf:summary",
      label: "Résumé",
      value: record.summary,
      datatype: "literal",
    });
  }

  if (record.classification) {
    workProperties.push({
      uri: "bf:classification",
      label: "Classification",
      value: record.classification,
      datatype: "literal",
    });
  }

  entities.push({
    type: "Work",
    uri: `${baseUri}#Work`,
    properties: workProperties,
  });

  // bf:Instance - The physical manifestation
  const instanceProperties: BibframeProperty[] = [
    { uri: "bf:title", label: "Titre", value: record.fullTitle, datatype: "literal" },
  ];

  if (record.isbn) {
    instanceProperties.push({
      uri: "bf:identifiedBy",
      label: "ISBN",
      value: record.isbn,
      datatype: "literal",
    });
  }

  if (record.issn) {
    instanceProperties.push({
      uri: "bf:identifiedBy",
      label: "ISSN",
      value: record.issn,
      datatype: "literal",
    });
  }

  if (record.publisher) {
    instanceProperties.push({
      uri: "bf:provisionActivity",
      label: "Éditeur",
      value: record.publisher,
      datatype: "literal",
    });
  }

  if (record.publicationYear) {
    instanceProperties.push({
      uri: "bf:provisionActivity",
      label: "Date de publication",
      value: record.publicationYear,
      datatype: "literal",
    });
  }

  if (record.pageCount) {
    instanceProperties.push({
      uri: "bf:extent",
      label: "Étendue",
      value: `${String(record.pageCount)} pages`,
      datatype: "literal",
    });
  }

  if (record.edition) {
    instanceProperties.push({
      uri: "bf:editionStatement",
      label: "Mention d'édition",
      value: record.edition,
      datatype: "literal",
    });
  }

  if (record.series) {
    instanceProperties.push({
      uri: "bf:seriesStatement",
      label: "Collection",
      value: record.series,
      datatype: "literal",
    });
    if (record.seriesNumber) {
      instanceProperties.push({
        uri: "bf:seriesEnumeration",
        label: "Numéro de collection",
        value: record.seriesNumber,
        datatype: "literal",
      });
    }
  }

  entities.push({
    type: "Instance",
    uri: `${baseUri}#Instance`,
    properties: instanceProperties,
  });

  // bf:Item - The specific copies
  record.items.forEach((item, index) => {
    const itemProperties: BibframeProperty[] = [
      {
        uri: "bf:itemOf",
        label: "Exemplaire de",
        value: `${baseUri}#Instance`,
        datatype: "uri",
      },
      {
        uri: "bf:barcode",
        label: "Code-barres",
        value: item.barcode,
        datatype: "literal",
      },
      {
        uri: "bf:heldBy",
        label: "Détenu par",
        value: item.location.name,
        datatype: "literal",
      },
    ];

    if (item.callNumber) {
      itemProperties.push({
        uri: "bf:shelfMark",
        label: "Cote",
        value: item.callNumber,
        datatype: "literal",
      });
    }

    itemProperties.push({
      uri: "bf:status",
      label: "Statut",
      value: item.statusLabel,
      datatype: "literal",
    });

    entities.push({
      type: "Item",
      uri: `${baseUri}#Item${String(index + 1)}`,
      properties: itemProperties,
    });
  });

  return entities;
}

export default RecordEditPage;
